/**
 * HUD - interface de combate (secao 4.18 do ARCHITECTURE.md).
 *
 * Tudo e DOM dentro de #ui-root, nunca sprite 3D. O container #hud fica com
 * pointer-events:none: o HUD JAMAIS pode roubar o mouse durante a partida.
 * Somente a tela final (#hud-end), que so aparece com o jogo parado, e clicavel.
 *
 * Regras de desempenho seguidas aqui:
 * - zero alocacao de objeto por frame: os vetores auxiliares sao temporarios de
 *   modulo e os elementos sao criados uma unica vez no init();
 * - toda escrita em DOM passa por _setText/_setStyle, que comparam com o ultimo
 *   valor escrito e so tocam no elemento quando ele realmente mudou (mexer em
 *   textContent/style todo frame forca recalculo de estilo a toa);
 * - a mira e movida por DUAS variaveis CSS no container (--gap e --len) em vez
 *   de quatro transforms, entao o custo por frame e uma escrita, nao quatro.
 *
 * Nada aqui pode lancar excecao no laco de frame: update() e envolvido por um
 * try/catch que avisa uma unica vez e desliga o HUD se o erro for persistente.
 */

import * as THREE from 'three';
import { GAME, PLAYER } from '../core/Constants.js';

/* ------------------------------------------------------------------------- */
/* temporarios de modulo (NUNCA alocar dentro de update)                      */
/* ------------------------------------------------------------------------- */
const _dir = new THREE.Vector3();
const _hitPoint = new THREE.Vector3();

const DEG = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;
const TAU = Math.PI * 2;

/**
 * Icones de arma desenhados em SVG - nenhum asset externo. Cano para a direita.
 *
 * REGRA DE CONSTRUCAO (o icone e renderizado a 22px de altura, ou seja ~0,85
 * pixel por unidade do viewBox de 26): a silhueta tem que ser FECHADA. Na
 * rodada 2 o trilho ficava 0,7 unidade acima do corpo e a boca do cano era um
 * retangulo separado - a 22px isso vira meio pixel de vao, o antialias pinta
 * uma costura escura e o icone le como pecas soltas flutuando. Aqui toda peca
 * SOBREPOE a vizinha em pelo menos 0,4 unidade, nenhuma feicao tem menos de
 * 2 unidades de espessura e cano + boca sao um unico poligono.
 */
const WEAPON_ICONS = {
  rifle:
    '<svg viewBox="0 0 68 26" aria-hidden="true">' +
    '<polygon points="2,10 17,10 17,17.4 9,17.4 2,14"/>' +
    '<rect x="15" y="8" width="29" height="8"/>' +
    '<rect x="20" y="5.6" width="18" height="2.8"/>' +
    '<polygon points="43,9.6 61,9.6 61,7.8 65.4,7.8 65.4,15.4 61,15.4 61,13.6 43,13.6"/>' +
    '<polygon points="50,10 53,10 53,5.2 50,5.2"/>' +
    '<polygon points="24,15 33,15 31,25 21,25"/>' +
    '<polygon points="34,15 40,15 37,24 33,24"/>' +
    '</svg>',
  smg:
    '<svg viewBox="0 0 54 26" aria-hidden="true">' +
    '<rect x="1" y="8.6" width="10" height="3"/>' +
    '<rect x="10" y="7" width="26" height="9"/>' +
    '<rect x="13" y="4.6" width="14" height="2.8"/>' +
    '<polygon points="35,9.8 46,9.8 46,8 49.6,8 49.6,15 46,15 46,13.2 35,13.2"/>' +
    '<polygon points="18,15 26,15 24,26 14,26"/>' +
    '<polygon points="27,15 33,15 30,24 26,24"/>' +
    '</svg>',
  dmr:
    '<svg viewBox="0 0 68 26" aria-hidden="true">' +
    '<polygon points="1,9 15,9 15,18 6,18 1,15"/>' +
    '<rect x="6" y="6.8" width="9" height="2.4"/>' +
    '<rect x="14" y="8" width="26" height="8"/>' +
    '<rect x="18" y="2.4" width="20" height="4"/>' +
    '<rect x="20.8" y="6" width="2.4" height="2.4"/>' +
    '<rect x="32.6" y="6" width="2.4" height="2.4"/>' +
    '<polygon points="39,9.8 61,9.8 61,8 65.4,8 65.4,15.2 61,15.2 61,13.4 39,13.4"/>' +
    '<polygon points="22,15 29,15 28,23 21,23"/>' +
    '<polygon points="30,15 36,15 33,24 29,24"/>' +
    '</svg>'
};

/** Arco do indicador de direcao do dano, desenhado no topo de uma caixa 200x200. */
const DMG_ARC_SVG =
  '<svg viewBox="0 0 200 200" aria-hidden="true">' +
  '<path class="arc-out" d="M 67.9 31.1 A 76 76 0 0 1 132.1 31.1"/>' +
  '<path class="arc-in"  d="M 67.9 31.1 A 76 76 0 0 1 132.1 31.1"/>' +
  '<polygon class="arc-tip" points="100,10 93,22 107,22"/>' +
  '</svg>';

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

/* ------------------------------------------------------------------------- */
/* postos do banner central                                                   */
/* Um unico elemento serve a tres tipos de aviso e eles chegam no MESMO tick   */
/* (o GameState emite WAVE_START e, na linha seguinte, um NOTICE com o mesmo   */
/* conteudo). Sem posto, o aviso generico apagava o banner da onda - subtitulo */
/* e cor ambar inclusos - antes do primeiro frame ser pintado.                 */
/* ------------------------------------------------------------------------- */
const BANNER_TICK = 1;    // contagem regressiva: e relogio, nao anuncio
const BANNER_NOTICE = 2;  // aviso generico do jogo
const BANNER_EVENT = 3;   // inicio/fim de onda: o anuncio de verdade

/** "ONDA 2 EM 4", "RESPAWN EM 3" - qualquer aviso que termine em contagem. */
const TICK_RE = /(?:^|\s)EM\s+\d{1,2}$/;

export class HUD {
  constructor(ctx) {
    this.ctx = ctx;

    // o HUD continua vivo com o jogo pausado (fade da pausa, tela final, FPS)
    this.updateWhilePaused = true;

    this.el = {};
    this._built = false;
    this._matchLabels = false;   // painel esta no modo deathmatch?
    this._offs = [];
    this._failures = 0;
    this._warned = false;

    // 'boot' e nao 'menu': o main.js emite GAME_STATE {menu} logo apos o boot e
    // esse primeiro evento precisa ser processado, nao descartado como repetido.
    this.state = 'boot';

    // mira
    this._gap = 7;
    this._len = 7;
    this._gapWritten = -1;
    this._lenWritten = -1;
    this._fireKick = 0;
    this._enemyAim = false;
    this._probeFrame = 0;
    this._probeBroken = false;

    // dano
    this._hitFlash = 0;
    this._vignette = -1;
    this._dmgDirs = [];      // { angle, life, max, el, wroteAngle, wroteAlpha }

    // banner central: posto do aviso na tela e carencia dele
    this._bannerRank = 0;
    this._bannerHold = 0;

    // temporizadores
    this._hitmarkerT = 0;
    this._noticeT = 0;
    this._deathT = 0;
    this._deathSec = -1;
    this._fpsT = 0;

    // ultimos valores escritos (evitam montar string identica todo frame)
    this._hpWritten = -1;
    this._flashWritten = -1;

    // estado da partida espelhado dos eventos
    this._wave = 0;
    // Ha onda EM CURSO? O destaque ambar de "hostis zerados" e conquista, e so
    // vale entre o inicio e a limpeza da onda - no intervalo tambem nao ha bot
    // vivo, e acender ambar ali gasta a unica cor de alerta do HUD a toa.
    this._waveActive = false;
    this._kills = 0;
    this._reloading = false;
    this._killfeed = [];

    this._vh = window.innerHeight || 1080;
  }

  /* ===================================================================== */
  /* construcao                                                             */
  /* ===================================================================== */
  async init() {
    const root = document.getElementById('ui-root') || document.body;

    const hud = document.createElement('div');
    hud.id = 'hud';
    hud.innerHTML = this._markup();
    root.appendChild(hud);

    const end = document.createElement('div');
    end.id = 'hud-end';
    end.innerHTML = this._endMarkup();
    root.appendChild(end);

    const q = (id) => document.getElementById(id);
    this.el = {
      hud,
      end,
      crosshair: q('hud-crosshair'),
      hitmarker: q('hud-hitmarker'),
      vignette: q('hud-vignette'),
      hitflash: q('hud-hitflash'),
      dmgDirs: q('hud-dmg-dirs'),
      healthValue: q('hud-health-value'),
      healthFill: q('hud-health-fill'),
      ammoBox: q('hud-ammo'),
      ammoMag: q('hud-ammo-mag'),
      ammoReserve: q('hud-ammo-reserve'),
      weaponName: q('hud-weapon-name'),
      weaponMode: q('hud-weapon-mode'),
      weaponIcon: q('hud-weapon-icon'),
      reloadHint: q('hud-reload-hint'),
      wave: q('hud-wave'),
      enemies: q('hud-enemies'),
      score: q('hud-score'),
      k1: q('hud-k1'),
      k2: q('hud-k2'),
      k3: q('hud-k3'),
      fps: q('hud-fps'),
      killfeed: q('hud-killfeed'),
      banner: q('hud-banner'),
      bannerTitle: q('hud-banner-title'),
      bannerSub: q('hud-banner-sub'),
      death: q('hud-death'),
      deathSub: q('hud-death-sub'),
      endTitle: q('hud-end-title'),
      endEyebrow: q('hud-end-eyebrow'),
      endWave: q('hud-end-wave'),
      endScore: q('hud-end-score'),
      endKills: q('hud-end-kills'),
      btnRestart: q('hud-btn-restart'),
      btnMenu: q('hud-btn-menu')
    };

    // pool fixo de 4 indicadores de direcao de dano (sem alocar por golpe)
    for (let i = 0; i < 4; i++) {
      const d = document.createElement('div');
      d.className = 'dmg-dir';
      d.innerHTML = DMG_ARC_SVG;
      this.el.dmgDirs.appendChild(d);
      this._dmgDirs.push({ el: d, world: 0, life: 0, max: 1, wroteAngle: 999, wroteAlpha: -1 });
    }

    this.el.btnRestart.addEventListener('click', () => this._restart());
    this.el.btnMenu.addEventListener('click', () => this._toMenu());

    this._bindEvents();
    this._applySettings();
    this._offs.push(this.ctx.settings.onChange(() => this._applySettings()));

    this._built = true;
    this._syncWeapon(true);
  }

  _markup() {
    return (
      '<div id="hud-vignette"></div>' +
      '<div id="hud-hitflash"></div>' +
      '<div id="hud-pulse"></div>' +
      '<div id="hud-dmg-dirs"></div>' +

      '<div id="hud-crosshair">' +
        '<i class="ch-line ch-top"></i><i class="ch-line ch-bottom"></i>' +
        '<i class="ch-line ch-left"></i><i class="ch-line ch-right"></i>' +
        '<i class="ch-dot"></i>' +
      '</div>' +

      '<svg id="hud-hitmarker" viewBox="0 0 48 48" aria-hidden="true">' +
        '<line x1="11" y1="11" x2="19" y2="19"/><line x1="37" y1="11" x2="29" y2="19"/>' +
        '<line x1="11" y1="37" x2="19" y2="29"/><line x1="37" y1="37" x2="29" y2="29"/>' +
      '</svg>' +

      // sem linha intermediaria: os seis campos entram direto na grade de duas
      // colunas do #hud-stats, senao cada linha mede a propria largura e os
      // tres valores nascem em x diferentes
      // Os rotulos tem id porque o painel serve aos DOIS modos: na campanha
      // e ONDA/HOSTIS/PONTOS, no deathmatch e TEMPO/BAIXAS/PONTOS.
      '<div id="hud-stats">' +
        '<span class="hud-k" id="hud-k1">ONDA</span><span class="hud-v" id="hud-wave">--</span>' +
        '<span class="hud-k" id="hud-k2">HOSTIS</span><span class="hud-v" id="hud-enemies">--</span>' +
        '<span class="hud-k" id="hud-k3">PONTOS</span><span class="hud-v" id="hud-score">0</span>' +
      '</div>' +
      '<div id="hud-fps">-- FPS</div>' +

      '<div id="hud-killfeed"></div>' +

      '<div id="hud-banner"><div id="hud-banner-title"></div><div id="hud-banner-sub"></div></div>' +

      '<div id="hud-health">' +
        '<div class="h-head"><span class="hud-k">VITALIDADE</span>' +
        '<span id="hud-health-value">100</span></div>' +
        '<div class="h-bar"><i id="hud-health-fill"></i><span class="h-ticks"></span></div>' +
      '</div>' +

      '<div id="hud-ammo">' +
        '<div class="a-head"><span id="hud-weapon-mode">AUTO</span>' +
        '<span id="hud-weapon-name">SEM ARMA</span></div>' +
        '<div id="hud-weapon-icon"></div>' +
        '<div class="a-count"><span id="hud-ammo-mag">--</span>' +
        '<span class="a-sep">/</span><span id="hud-ammo-reserve">--</span></div>' +
        '<div id="hud-reload-hint">RECARREGAR [R]</div>' +
      '</div>' +

      '<div id="hud-death">' +
        '<div class="death-title">ABATIDO</div>' +
        '<div class="death-sub" id="hud-death-sub">REINSER\u00c7\u00c3O EM 3</div>' +
      '</div>'
    );
  }

  /**
   * ACENTUACAO: identificador e comentario seguem em ASCII, mas TEXTO QUE APARECE
   * NA TELA leva acento correto de portugues. Nas strings o acento entra como
   * escape unicode (\u00c7 = C-cedilha, \u00c3 = A-til, \u00d3 = O-agudo, \u00cd =
   * I-agudo, \u00c2 = A-circunflexo, \u00ca = E-circunflexo, \u00d5 = O-til,
   * \u00da = U-agudo): o arquivo continua ASCII puro no disco e o jogador ve
   * "MISSAO" escrito certo. Vale para o markup e para todo _setText daqui.
   */
  _endMarkup() {
    return (
      '<div class="end-card">' +
        '<div class="end-eyebrow" id="hud-end-eyebrow">RELAT\u00d3RIO DE OPERA\u00c7\u00c3O</div>' +
        '<h2 class="end-title" id="hud-end-title">MISS\u00c3O ENCERRADA</h2>' +
        '<div class="end-grid">' +
          '<div class="end-stat"><span class="hud-k">ONDA</span><b id="hud-end-wave">0</b></div>' +
          '<div class="end-stat"><span class="hud-k">ABATES</span><b id="hud-end-kills">0</b></div>' +
          '<div class="end-stat"><span class="hud-k">PONTOS</span><b id="hud-end-score">0</b></div>' +
        '</div>' +
        '<div class="end-actions">' +
          '<button type="button" class="btn primary" id="hud-btn-restart">REINICIAR</button>' +
          '<button type="button" class="btn ghost" id="hud-btn-menu">MENU</button>' +
        '</div>' +
      '</div>'
    );
  }

  /* ===================================================================== */
  /* eventos                                                                */
  /* ===================================================================== */
  _bindEvents() {
    const { events, EVENTS: EV } = this.ctx;
    const on = (type, fn) => this._offs.push(events.on(type, fn));

    on(EV.GAME_STATE, (p) => this._onState(p?.state));

    on(EV.HITMARKER, (p) => this._hitmarkerShow(!!p?.headshot, !!p?.killed));

    on(EV.KILLFEED, (p) => this._killfeedAdd(p));

    on(EV.NOTICE, (p) => {
      if (!p?.text) return;
      const text = String(p.text);
      // p.tick vem pronto se quem emitiu marcar; senao a forma do texto entrega.
      const tick = p.tick === true || TICK_RE.test(text);

      // Aviso de duas ideias numa linha ("PREPARE-SE - ONDA 1 DE 10") chegava ao
      // banner como titulo unico e sub vazio: o subtitulo colapsava e sobrava
      // UMA tira de texto pousada no meio do ceu, que le como toast de debug.
      // Quem emite separa os dois conceitos por " - " (espaco-hifen-espaco), e e
      // exatamente esse o corte - hifen de palavra composta ("PREPARE-SE") nao
      // tem espaco em volta, entao nao e afetado. O relogio do intervalo fica de
      // fora: ali o texto e frase curta e unica, e nao quer subtitulo nenhum.
      let title = text;
      let sub = typeof p.sub === 'string' ? p.sub : '';
      if (!tick && !sub) {
        const cut = text.indexOf(' - ');
        if (cut > 0 && cut + 3 < text.length) {
          title = text.slice(0, cut);
          sub = text.slice(cut + 3);
        }
      }

      this._banner(title, sub, tick ? BANNER_TICK : BANNER_NOTICE,
        Math.max(0.6, Number(p.duration) || 2.2));
    });

    on(EV.WAVE_START, (p) => {
      this._wave = Number(p?.wave) || (this._wave + 1);
      this._waveActive = true;
      const n = Number(p?.enemies);
      this._banner('ONDA ' + this._wave,
        Number.isFinite(n) ? n + ' HOSTIS DETECTADOS' : 'HOSTIS DETECTADOS',
        BANNER_EVENT, 2.6);
    });

    on(EV.WAVE_CLEAR, (p) => {
      const w = Number(p?.wave) || this._wave;
      this._waveActive = false;
      this._banner('SETOR LIMPO', 'ONDA ' + w + ' CONCLU\u00cdDA', BANNER_EVENT, 2.4);
    });

    on(EV.PLAYER_DAMAGED, (p) => this._onDamaged(p));

    on(EV.PLAYER_DIED, () => {
      this._deathT = GAME.RESPAWN_DELAY;
      this._hitFlash = 1;
      this.el.death.classList.add('show');
    });

    on(EV.ENTITY_KILLED, (p) => {
      if (p && p.source === this.ctx.player) this._kills++;
    });

    on(EV.WEAPON_FIRE, () => { this._fireKick = Math.min(1, this._fireKick + 0.55); });
    on(EV.WEAPON_SWITCH, () => { this._reloading = false; this._syncWeapon(true); });
    on(EV.WEAPON_RELOAD_START, () => { this._reloading = true; });
    on(EV.WEAPON_RELOAD_END, () => { this._reloading = false; });
    on(EV.WEAPON_DRY, () => { this._fireKick = Math.min(1, this._fireKick + 0.2); });
  }

  _onState(state) {
    if (!state || state === this.state) return;
    this.state = state;
    const { hud, end } = this.el;

    hud.classList.toggle('show', state === 'playing' || state === 'paused');
    hud.classList.toggle('paused', state === 'paused');

    if (state === 'gameover' || state === 'victory') {
      this._waveActive = false;
      this._showEnd(state === 'victory');
    } else {
      end.classList.remove('show');
    }

    if (state === 'playing') {
      this.el.death.classList.remove('show');
    }
    if (state === 'menu') {
      this._deathT = 0;
      this.el.death.classList.remove('show');
      this._clearKillfeed();
      this._kills = 0;
      this._wave = 0;
      this._waveActive = false;
    }
  }

  _onDamaged(p) {
    this._hitFlash = Math.min(1, this._hitFlash + 0.75);
    const from = p?.from;
    if (!from || typeof from.x !== 'number') return;

    const cam = this.ctx.camera;
    const dx = from.x - cam.position.x;
    const dz = from.z - cam.position.z;
    if (dx * dx + dz * dz < 1e-4) return;

    // Guarda o angulo NO MUNDO, nao na tela: o arco tem que continuar apontando
    // para o agressor enquanto o jogador gira. Quem converte para tela e o frame.
    const world = Math.atan2(dx, dz);

    // reaproveita o slot que aponta para quase a mesma direcao, senao o mais gasto
    let slot = null;
    let worst = Infinity;
    for (let i = 0; i < this._dmgDirs.length; i++) {
      const d = this._dmgDirs[i];
      if (d.life > 0 && Math.abs(this._wrapPi(d.world - world)) < 0.42) { slot = d; break; }
      if (d.life < worst) { worst = d.life; slot = d; }
    }
    slot.world = world;
    slot.max = 1.35;
    slot.life = slot.max;
  }

  /** Normaliza um angulo em radianos para o intervalo -PI..PI. */
  _wrapPi(a) {
    while (a > Math.PI) a -= TAU;
    while (a < -Math.PI) a += TAU;
    return a;
  }

  /* ===================================================================== */
  /* laco de frame                                                          */
  /* ===================================================================== */
  update(dt) {
    if (!this._built || this._failures > 10) return;
    try {
      this._updateCrosshair(dt);
      this._updateVitals(dt);
      this._updateAmmo();
      this._updateStats();
      this._updateDamageDirs(dt);
      this._updateTimers(dt);
      this._updateFps(dt);
      this._failures = 0;
    } catch (err) {
      this._failures++;
      if (!this._warned) {
        this._warned = true;
        console.warn('[HUD] falha durante o update, degradando em silencio:', err);
      }
    }
  }

  onResize(w, h) {
    this._vh = h || window.innerHeight || 1080;
  }

  /* --- mira -------------------------------------------------------------- */
  _updateCrosshair(dt) {
    const el = this.el.crosshair;
    const w = this.ctx.weapons;
    const settings = this.ctx.settings;

    if (!settings.crosshairEnabled || this.state !== 'playing') {
      this._flag(el, 'gone', true);
      return;
    }

    const cur = w?.current;
    const ads = !!w?.isADS;
    const scoped = ads && this._isScoped(cur);

    // Mira com luneta: o HUD sai de cena e quem manda e a otica do ViewModel.
    this._flag(el, 'gone', scoped);
    this._flag(el, 'faded', ads && !scoped);
    if (scoped) return;

    let spread = w?.getSpread?.();
    if (typeof spread !== 'number' || !Number.isFinite(spread) || spread < 0) spread = 0.011;
    // defesa: valor acima de ~23 graus nao e spread em radiano, e grau.
    if (spread > 0.4) spread *= DEG;

    // converte o angulo real de dispersao para pixels na tela do jogador
    const halfH = this._vh * 0.5;
    const fovHalf = Math.tan((this.ctx.camera.fov * DEG) * 0.5);
    const px = (Math.tan(clamp(spread, 0, 0.5)) / (fovHalf || 0.7)) * halfH;

    this._fireKick = Math.max(0, this._fireKick - dt * 3.4);
    const target = clamp(3.5 + px + this._fireKick * 6, 3.5, halfH * 0.5);

    // sobe rapido (o recuo tem que ser visivel) e desce devagar
    const k = target > this._gap ? 24 : 9;
    this._gap += (target - this._gap) * Math.min(1, k * dt);

    const lenTarget = 6 + clamp((this._gap - 4) * 0.09, 0, 4);
    this._len += (lenTarget - this._len) * Math.min(1, 8 * dt);

    if (Math.abs(this._gap - this._gapWritten) > 0.3) {
      this._gapWritten = this._gap;
      el.style.setProperty('--gap', this._gap.toFixed(1) + 'px');
    }
    if (Math.abs(this._len - this._lenWritten) > 0.3) {
      this._lenWritten = this._len;
      el.style.setProperty('--len', this._len.toFixed(1) + 'px');
    }

    this._probeEnemy();
    this._flag(el, 'enemy', this._enemyAim);
  }

  /**
   * Uma arma "com luneta" some da mira do HUD. O dado JA declara isso: cada arma
   * de WeaponsData traz `hasScope`, entao e por ele que se comeca.
   *
   * ARMADILHA: `adsFov` nao e zoom, e o FOV alvo da camera EM GRAUS (KR-7 52,
   * VP-9 58, SR-12 24). Lido como multiplicador, qualquer arma passava do limiar
   * de 1.4 e o HUD tratava as tres como se tivessem otica - dava icone de fuzil
   * de precisao ao KR-7 e, pior, APAGAVA a mira no ADS de mira de ferro.
   */
  _isScoped(def) {
    if (!def) return false;
    if (typeof def.hasScope === 'boolean') return def.hasScope;
    if (def.scoped === true || def.scope === true) return true;
    const z = def.adsZoom ?? def.zoom ?? def.scopeZoom;
    return typeof z === 'number' && z >= 1.4;
  }

  /**
   * Mira sobre inimigo -> mira vermelha. Feito a cada 4 frames: e um raycast,
   * nao precisa de 60 Hz para o olho perceber.
   */
  _probeEnemy() {
    if (this._probeBroken) return;
    if ((this._probeFrame = (this._probeFrame + 1) & 3) !== 0) return;

    const targets = this.ctx.targets;
    if (!targets?.raycast) { this._enemyAim = false; return; }

    try {
      const cam = this.ctx.camera;
      const e = cam.matrixWorld.elements;
      _dir.set(-e[8], -e[9], -e[10]).normalize();   // eixo -Z da camera = frente

      const team = this.ctx.player?.team ?? 0;
      const hit = targets.raycast(cam.position, _dir, 160, team);
      if (!hit || !hit.point) { this._enemyAim = false; return; }

      // parede entre o cano e o alvo cancela o destaque
      const los = this.ctx.collision?.lineOfSight;
      if (los) {
        _hitPoint.copy(hit.point);
        this._enemyAim = !!this.ctx.collision.lineOfSight(cam.position, _hitPoint);
      } else {
        this._enemyAim = true;
      }
    } catch (err) {
      this._probeBroken = true;
      this._enemyAim = false;
      console.warn('[HUD] deteccao de inimigo sob a mira desligada:', err);
    }
  }

  /* --- vida, vinheta e batimento ---------------------------------------- */
  _updateVitals(dt) {
    const p = this.ctx.player;
    const max = (p && p.maxHealth) || PLAYER.MAX_HEALTH;
    const hpRaw = p && typeof p.health === 'number' ? p.health : max;
    const hp = clamp(hpRaw, 0, max);
    const t = hp / max;

    // so monta string quando o valor mudou de verdade
    if (Math.abs(t - this._hpWritten) > 0.0015) {
      this._hpWritten = t;
      this._setNum(this.el.healthValue, Math.ceil(hp));
      this.el.healthFill.style.transform = 'scaleX(' + t.toFixed(3) + ')';
    }

    const hud = this.el.hud;
    const critical = t <= 0.3;
    this._flag(hud, 'critical', critical);
    this._flag(hud, 'low', !critical && t <= 0.6);

    // vinheta permanente cresce conforme a vida cai; o flash e o golpe recente
    this._hitFlash = Math.max(0, this._hitFlash - dt * 2.1);
    // o "|| valor === 0" garante que o ultimo passo ate zero seja escrito, senao
    // sobra uma pelicula vermelha de opacidade minima que nunca sai da tela
    const v = t >= 0.72 ? 0 : clamp(Math.pow((0.72 - t) / 0.72, 1.35) * 0.82, 0, 1);
    if (v !== this._vignette && (v === 0 || Math.abs(v - this._vignette) > 0.012)) {
      this._vignette = v;
      this.el.vignette.style.opacity = v.toFixed(2);
    }
    const flash = this._hitFlash * 0.7;
    if (flash !== this._flashWritten && (flash === 0 || Math.abs(flash - this._flashWritten) > 0.015)) {
      this._flashWritten = flash;
      this.el.hitflash.style.opacity = flash.toFixed(2);
    }
  }

  /* --- municao ----------------------------------------------------------- */
  _updateAmmo() {
    const w = this.ctx.weapons;
    this._syncWeapon(false);

    const mag = typeof w?.ammo === 'number' ? w.ammo : null;
    const res = typeof w?.reserve === 'number' ? w.reserve : null;

    if (mag === null) this._setText(this.el.ammoMag, '--'); else this._setNum(this.el.ammoMag, mag);
    if (res === null) this._setText(this.el.ammoReserve, '--'); else this._setNum(this.el.ammoReserve, res);

    const box = this.el.ammoBox;
    const empty = mag === 0;
    this._flag(box, 'empty', empty);
    this._flag(box, 'low', !empty && mag !== null && mag > 0 && mag <= Math.max(3, Math.round((this._magSize || 30) * 0.25)));
    this._flag(box, 'reloading', this._reloading);

    this._setText(this.el.reloadHint, this._reloading ? 'RECARREGANDO' : 'RECARREGAR [R]');
  }

  /** Le a arma atual e redesenha nome/modo/icone. So faz trabalho quando muda. */
  _syncWeapon(force) {
    const cur = this.ctx.weapons?.current;
    if (!force && cur === this._lastWeapon) return;
    this._lastWeapon = cur;

    const name = this._weaponName(cur);
    this._setText(this.el.weaponName, name);

    const mode = this._weaponMode(cur);
    this._setText(this.el.weaponMode, mode);
    this._setStyle(this.el.weaponMode, 'display', mode ? '' : 'none');

    this._magSize = Number(cur?.magazine ?? cur?.magSize ?? cur?.clip ?? cur?.ammo) || 30;

    const kind = this._weaponKind(cur, name);
    if (kind !== this._iconKind) {
      this._iconKind = kind;
      this.el.weaponIcon.innerHTML = WEAPON_ICONS[kind] || WEAPON_ICONS.rifle;
    }
  }

  _weaponName(def) {
    if (!def) return 'SEM ARMA';
    // no killfeed a arma pode chegar como string em vez do objeto de definicao
    if (typeof def === 'string') return def.toUpperCase();
    const n = def.name ?? def.label ?? def.displayName ?? def.id;
    return typeof n === 'string' && n.length ? n.toUpperCase() : 'ARMA';
  }

  /**
   * Selo de modo de disparo. O contrato nao fixa o nome do campo, e "type" tanto
   * pode ser o modo quanto a CLASSE da arma - por isso so aceitamos valores que
   * sao reconhecidamente modo de disparo; qualquer outra coisa esconde o selo.
   */
  _weaponMode(def) {
    if (!def) return '';
    const m = def.fireMode ?? def.fire ?? def.mode ?? def.type;
    if (typeof m !== 'string' || m.length > 12) return '';
    const s = m.toUpperCase();
    if (s === 'AUTO' || s === 'AUTOMATIC' || s === 'FULLAUTO' || s === 'FULL_AUTO') return 'AUTO';
    if (s === 'SEMI' || s === 'SEMIAUTO' || s === 'SEMI_AUTO' || s === 'SINGLE') return 'SEMI';
    if (s === 'BURST') return 'RAJADA';
    if (s === 'PUMP' || s === 'SHOTGUN') return 'BOMBA';
    if (s === 'BOLT') return 'FERROLHO';
    return '';
  }

  _weaponKind(def, name) {
    const tag = ((def?.class ?? def?.category ?? def?.kind ?? '') + ' ' + (name || '')).toUpperCase();
    if (/SMG|SUBMETRAL|MP|UZI|PISTOL/.test(tag)) return 'smg';
    if (/DMR|SNIP|PRECIS|MARKS|ESPING|SHOT|CALIBRE/.test(tag)) return 'dmr';
    if (def && this._isScoped(def)) return 'dmr';
    return 'rifle';
  }

  /* --- onda, hostis e pontuacao ------------------------------------------ */
  _updateStats() {
    // PARTIDA ONLINE: onda e hostis nao existem no deathmatch. Sem esta ponta o
    // HUD ficava com "ONDA --" e "HOSTIS --" fixos e PONTOS zerado a partida
    // inteira, enquanto o placar de verdade so aparecia segurando Tab.
    if (this.ctx.match?.isActive?.()) { this._updateMatchStats(); return; }
    if (this._matchLabels) this._restoreCampaignLabels();

    const wave = this._wave || this.ctx.game?.wave || 0;
    if (wave > 0) this._setNum(this.el.wave, wave); else this._setText(this.el.wave, '--');

    const n = this.ctx.bots?.count?.();
    const hasN = typeof n === 'number' && Number.isFinite(n);
    if (hasN) this._setNum(this.el.enemies, n); else this._setText(this.el.enemies, '--');
    // ambar so quando a onda EM CURSO foi de fato neutralizada, nunca no
    // intervalo (onde tambem nao ha bot vivo e nada aconteceu ainda)
    this._flag(this.el.enemies, 'zero', hasN && n === 0 && this._waveActive);

    const score = this.ctx.player?.score ?? this.ctx.game?.score ?? 0;
    this._setNum(this.el.score, Math.max(0, Math.round(score)));
  }

  /** Painel do deathmatch: tempo restante, baixas e pontos, tudo do servidor. */
  _updateMatchStats() {
    const st = this.ctx.match?.getStatus?.();
    if (!st) return;

    if (!this._matchLabels) {
      this._setText(this.el.k1, 'TEMPO');
      this._setText(this.el.k2, 'BAIXAS');
      this._setText(this.el.k3, 'PONTOS');
      this._matchLabels = true;
    }

    const t = Math.max(0, Math.floor(st.timeLeft || 0));
    const mm = Math.floor(t / 60);
    const ss = t % 60;
    this._setText(this.el.wave, `${mm}:${ss < 10 ? '0' : ''}${ss}`);
    // Ambar no ultimo minuto: e a hora em que o tempo passa a valer decisao.
    this._flag(this.el.wave, 'zero', t > 0 && t <= 60);

    this._setNum(this.el.enemies, Math.max(0, st.kills | 0));
    this._flag(this.el.enemies, 'zero', false);
    this._setNum(this.el.score, Math.max(0, Math.round(st.score || 0)));
  }

  /** Devolve os rotulos da campanha ao sair de uma partida online. */
  _restoreCampaignLabels() {
    this._setText(this.el.k1, 'ONDA');
    this._setText(this.el.k2, 'HOSTIS');
    this._setText(this.el.k3, 'PONTOS');
    this._flag(this.el.wave, 'zero', false);
    this._matchLabels = false;
  }

  /* --- indicadores de direcao do dano ------------------------------------ */
  _updateDamageDirs(dt) {
    // frente da camera em angulo de mundo (ordem YXZ: rotation.y e o yaw, e a
    // frente da camera e -Z, dai o +PI)
    const forward = this.ctx.camera.rotation.y + Math.PI;

    for (let i = 0; i < this._dmgDirs.length; i++) {
      const d = this._dmgDirs[i];
      if (d.life <= 0) continue;
      d.life -= dt;
      if (d.life <= 0) {
        d.life = 0;
        if (d.wroteAlpha !== 0) { d.wroteAlpha = 0; d.el.style.opacity = '0'; }
        continue;
      }

      const k = d.life / d.max;
      const alpha = Math.round(clamp(k * k * 1.15, 0, 1) * 50) / 50;   // passos de 0.02
      if (alpha !== d.wroteAlpha) { d.wroteAlpha = alpha; d.el.style.opacity = String(alpha); }

      // 0 grau = fonte a frente, +90 = a direita da tela
      const angle = -this._wrapPi(d.world - forward) * RAD2DEG;
      if (Math.abs(angle - d.wroteAngle) > 0.5) {
        d.wroteAngle = angle;
        d.el.style.transform = 'rotate(' + angle.toFixed(1) + 'deg)';
      }
    }
  }

  /* --- temporizadores de banner, hitmarker, morte e killfeed -------------- */
  _updateTimers(dt) {
    if (this._hitmarkerT > 0) {
      this._hitmarkerT -= dt;
      // atencao: em SVG className e somente leitura (SVGAnimatedString)
      if (this._hitmarkerT <= 0) this.el.hitmarker.setAttribute('class', '');
    }

    if (this._noticeT > 0) {
      this._noticeT -= dt;
      if (this._bannerHold > 0) this._bannerHold -= dt;
      if (this._noticeT <= 0) {
        this._bannerRank = 0;
        this._bannerHold = 0;
        this.el.banner.classList.remove('show');
      }
    }

    if (this._deathT > 0) {
      this._deathT -= dt;
      const s = Math.max(0, Math.ceil(this._deathT));
      if (s !== this._deathSec) {
        this._deathSec = s;
        this._setText(this.el.deathSub, s > 0 ? 'REINSER\u00c7\u00c3O EM ' + s : 'REINSERINDO');
      }
      if (this._deathT <= 0) this.el.death.classList.remove('show');
    } else if (this.ctx.player && this.ctx.player.alive === true) {
      if (this.el.death.classList.contains('show')) this.el.death.classList.remove('show');
    }

    for (let i = this._killfeed.length - 1; i >= 0; i--) {
      const k = this._killfeed[i];
      k.t -= dt;
      if (k.t <= 0 && !k.out) {
        k.out = true;
        k.t = 0.32;
        k.el.classList.add('out');
      } else if (k.t <= 0 && k.out) {
        k.el.remove();
        this._killfeed.splice(i, 1);
      }
    }
  }

  _updateFps(dt) {
    if (!this.ctx.settings.showFps) return;
    this._fpsT -= dt;
    if (this._fpsT > 0) return;
    this._fpsT = 0.25;
    this._setText(this.el.fps, Math.round(this.ctx.engine.fps) + ' FPS');
  }

  /* ===================================================================== */
  /* feedback pontual                                                       */
  /* ===================================================================== */
  _hitmarkerShow(headshot, killed) {
    const el = this.el.hitmarker;
    el.setAttribute('class', '');
    // reflow forcado: sem isso a animacao nao reinicia em acertos seguidos
    void el.getBoundingClientRect().width;
    el.setAttribute('class', killed ? 'hit kill' : headshot ? 'hit head' : 'hit');
    this._hitmarkerT = killed ? 0.24 : 0.16;
  }

  _killfeedAdd(p) {
    if (!p) return;
    const killerIsPlayer = p.killer === this.ctx.player || p.killer === 'player';
    const victimIsPlayer = p.victim === this.ctx.player || p.victim === 'player';

    const row = document.createElement('div');
    row.className = 'kf-row' + (killerIsPlayer ? ' by-player' : '') + (victimIsPlayer ? ' on-player' : '');

    const a = document.createElement('span');
    a.className = 'kf-name' + (killerIsPlayer ? ' me' : '');
    a.textContent = this._nameOf(p.killer, 'DESCONHECIDO');
    row.appendChild(a);

    if (p.headshot) {
      const hs = document.createElement('span');
      hs.className = 'kf-head';
      hs.textContent = 'HS';
      row.appendChild(hs);
    }

    const ic = document.createElement('span');
    ic.className = 'kf-icon';
    ic.innerHTML = WEAPON_ICONS[this._weaponKind(p.weapon, this._weaponName(p.weapon))] || WEAPON_ICONS.rifle;
    row.appendChild(ic);

    const b = document.createElement('span');
    b.className = 'kf-name victim' + (victimIsPlayer ? ' me' : '');
    b.textContent = this._nameOf(p.victim, 'HOSTIL');
    row.appendChild(b);

    this.el.killfeed.appendChild(row);
    this._killfeed.push({ el: row, t: 5.0, out: false });

    while (this._killfeed.length > 5) {
      const old = this._killfeed.shift();
      old.el.remove();
    }
  }

  _nameOf(x, fallback) {
    if (x === this.ctx.player || x === 'player') return 'VOC\u00ca';
    if (typeof x === 'string' && x.length) return x.toUpperCase();
    const n = x?.displayName ?? x?.name ?? x?.id;
    if (typeof n === 'string' && n.length) return n.toUpperCase();
    if (typeof n === 'number') return 'HOSTIL ' + n;
    return fallback;
  }

  _clearKillfeed() {
    for (let i = 0; i < this._killfeed.length; i++) this._killfeed[i].el.remove();
    this._killfeed.length = 0;
  }

  /**
   * Escreve o banner central respeitando o posto do aviso: um aviso de posto
   * MENOR nao derruba o que esta na tela enquanto durar a carencia. E o que
   * impede o NOTICE do mesmo tick de apagar o subtitulo e a cor da onda.
   */
  _banner(title, sub, rank, duration) {
    if (rank < this._bannerRank && this._bannerHold > 0) return;

    const dur = duration > 0 ? duration : 2.2;
    this._noticeT = dur;
    this._bannerRank = rank;
    // Anuncio de onda segura a tela inteira; aviso comum segura so o comeco,
    // para a contagem regressiva do intervalo poder assumir logo em seguida.
    this._bannerHold = rank >= BANNER_EVENT ? dur : (rank === BANNER_NOTICE ? 0.85 : 0);

    this._setText(this.el.bannerTitle, title);
    this._setText(this.el.bannerSub, sub || '');
    this._flag(this.el.banner, 'neutral', rank !== BANNER_EVENT);
    this._flag(this.el.banner, 'tick', rank === BANNER_TICK);
    this.el.banner.classList.add('show');
  }

  /* ===================================================================== */
  /* telas de fim de partida                                                */
  /* ===================================================================== */
  _showEnd(victory) {
    const end = this.el.end;
    end.classList.toggle('victory', victory);
    this._setText(this.el.endEyebrow, victory ? 'OPERA\u00c7\u00c3O BEM-SUCEDIDA' : 'OPERA\u00c7\u00c3O COMPROMETIDA');
    this._setText(this.el.endTitle, victory ? 'MISS\u00c3O CUMPRIDA' : 'FOR\u00c7A ELIMINADA');
    this._setText(this.el.endWave, String(this._wave || this.ctx.game?.wave || 0));
    this._setText(this.el.endKills, String(this._kills));
    this._setText(this.el.endScore, String(Math.max(0, Math.round(this.ctx.player?.score ?? this.ctx.game?.score ?? 0))));
    this.el.death.classList.remove('show');
    end.classList.add('show');
    // sem mouse capturado nao da para clicar em "reiniciar"
    this.ctx.input?.releaseLock?.();
  }

  _restart() {
    this.el.end.classList.remove('show');
    this._kills = 0;
    this._wave = 0;
    this._clearKillfeed();
    this.ctx.engine.paused = false;
    this.ctx.audio?.resume?.();
    this.ctx.game?.start?.();
    this.ctx.events.emit(this.ctx.EVENTS.GAME_STATE, { state: 'playing' });
    this.ctx.input?.requestLock?.();
  }

  _toMenu() {
    this.el.end.classList.remove('show');
    this.ctx.engine.paused = false;
    this.ctx.input?.releaseLock?.();
    this.ctx.events.emit(this.ctx.EVENTS.GAME_STATE, { state: 'menu' });
  }

  /* ===================================================================== */
  /* utilidades                                                             */
  /* ===================================================================== */
  _applySettings() {
    const s = this.ctx.settings;
    this.el.fps.classList.toggle('show', !!s.showFps);
    // O ponto central e opcional. Settings.js nao expoe essa chave hoje (e o
    // arquivo e do esqueleto, nao pode ser editado), entao o gancho fica pronto:
    // basta alguem fazer settings.set('crosshairDot', false) para o ponto sumir.
    this.el.crosshair.classList.toggle('no-dot', s.crosshairDot === false);
  }

  /** Escreve texto so quando mudou (evita layout thrash por frame). */
  _setText(el, str) {
    if (!el || el.__last === str) return;
    el.__last = str;
    el.__n = null;        // invalida o cache numerico do mesmo elemento
    el.textContent = str;
  }

  /**
   * Versao numerica: compara o NUMERO antes de montar a string, entao um valor
   * estavel (vida cheia, pontuacao parada) nao aloca nada por frame.
   */
  _setNum(el, n) {
    if (!el || el.__n === n) return;
    el.__n = n;
    el.__last = null;
    el.textContent = String(n);
  }

  /** classList.toggle so quando o estado realmente inverte. */
  _flag(el, cls, on) {
    if (!el) return;
    if (el.classList.contains(cls) === !!on) return;
    el.classList.toggle(cls, !!on);
  }

  _setStyle(el, prop, value) {
    if (!el) return;
    let cache = el.__sv;
    if (!cache) cache = el.__sv = Object.create(null);
    if (cache[prop] === value) return;
    cache[prop] = value;
    el.style[prop] = value;
  }

  dispose() {
    for (const off of this._offs) { try { off(); } catch { /* ignorado */ } }
    this._offs.length = 0;
    this._clearKillfeed();
    this.el.hud?.remove();
    this.el.end?.remove();
    this._built = false;
  }
}
